import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lifebuoy, CheckCircle, PaperPlaneRight, Image as ImageIcon, ThumbsUp, ThumbsDown, PencilSimple, Trash, Info } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";
import { AiChat } from "@/components/AiChat";
import api from "@/lib/api";

const CATEGORIES = [
  ["nfc_tag", "NFC-Tag funktioniert nicht"],
  ["buddy", "Problem mit einem SafeBuddy"],
  ["qr", "QR-Code / Link-Problem"],
  ["login", "Login / Passwort"],
  ["group", "Gruppen & Freigaben"],
  ["activation", "Aktivierungscode"],
  ["shop", "Shop / Bestellung"],
  ["other", "Sonstiges"],
];

const TROUBLE = {
  nfc_tag: ["Ist NFC an deinem Smartphone aktiviert? (Einstellungen → Verbindungen → NFC)", "Halte den Tag ruhig an die obere Rückseite des Geräts.", "Ist der zugehörige SafeBuddy aktiv und nicht gesperrt?"],
  buddy: ["Sind Name und Kontakte im Profil vollständig ausgefüllt?", "Ist „Anrufe erlauben“ bzw. „Nachrichten erlauben“ aktiviert?"],
  qr: ["Öffnet sich beim Scannen die Seite safebuddy…/t/…?", "Versuche, den Link direkt im Browser zu öffnen."],
  login: ["Stimmt deine E-Mail-Adresse genau?", "Nutze „Passwort vergessen?“ auf der Anmeldeseite, um es zurückzusetzen."],
  group: ["Ist die Gruppen-Funktion für dein Konto aktiv?", "Prüfe offene Einladungen und Übertragungsanfragen."],
  activation: ["Hast du den Code exakt eingegeben (Groß-/Kleinschreibung egal)?", "Wurde der Code evtl. schon einmal eingelöst?"],
  shop: ["Wurde die Zahlung bei PayPal abgeschlossen?", "Prüfe dein E-Mail-Postfach (auch Spam) auf die Bestätigung."],
  other: ["Beschreibe dein Anliegen bitte so genau wie möglich."],
};

const FAQ = [
  ["Mein NFC-Tag wird nicht erkannt", "Aktiviere NFC in den Geräteeinstellungen und halte den Tag ruhig an die obere Rückseite deines Smartphones. iPhones benötigen ggf. eine separate NFC-App zum Beschreiben."],
  ["Wie erstelle ich einen SafeBuddy?", "Gehe auf „SafeBuddys“, tippe auf „SafeBuddy erstellen“ und folge den Schritten. Danach kannst du den Tag beschreiben."],
  ["Ich habe mein Passwort vergessen", "Auf der Anmeldeseite findest du „Passwort vergessen?“. Wir senden dir einen Link zum Zurücksetzen per E-Mail."],
  ["Wozu dient der Aktivierungscode?", "Manche SafeBuddy-Tags werden mit einem Aktivierungscode ausgeliefert. Diesen gibst du beim Erstellen ein."],
];

function techContext() {  return {
    ua: navigator.userAgent,
    platform: navigator.platform || "",
    lang: navigator.language || "",
    screen: `${window.innerWidth}x${window.innerHeight}`,
    nfc: "NDEFReader" in window ? "yes" : "no",
    url: window.location.href,
    at: new Date().toISOString(),
  };
}

const MY_STATUS_LABELS = { OPEN: "Offen", IN_PROGRESS: "In Bearbeitung", WAITING_FOR_USER: "Warten auf dich", RESOLVED: "Gelöst", CLOSED: "Geschlossen" };
const MY_STATUS_COLORS = { OPEN: "#EF4444", IN_PROGRESS: "#F59E0B", WAITING_FOR_USER: "#0EA5E9", RESOLVED: "#22C55E", CLOSED: "#6B7280" };

export default function Support() {
  const { supportEnabled, content, lang, t } = useApp();
  const cc = content?.[lang] || content?.de;
  const catLabel = (v) => cc?.support?.categories?.[v] || CATEGORIES.find(([k]) => k === v)?.[1] || v;
  const faqList = (cc?.faq && cc.faq.length) ? cc.faq : FAQ.map(([q, a], i) => ({ id: String(i), q, a }));
  const [step, setStep] = useState("home"); // home | trouble | form | done
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [shot, setShot] = useState(null); // { path, name }
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();
  const [myTickets, setMyTickets] = useState([]);
  const [openTid, setOpenTid] = useState(null);
  const [editTid, setEditTid] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const delTimers = useRef({});

  const loadMine = () => { if (supportEnabled) api.get("/support/tickets/mine").then((r) => setMyTickets(r.data)).catch(() => {}); };

  const startEdit = (tk) => { setEditTid(tk.id); setEditDesc(tk.description || ""); };
  const saveEdit = async (tk) => {
    try { await api.put(`/support/tickets/${tk.id}/mine`, { description: editDesc }); setEditTid(null); loadMine(); toast.success(t("ticketUpdated")); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const doDelete = (tk) => {
    setConfirmDel(null);
    setMyTickets((prev) => prev.filter((x) => x.id !== tk.id));
    let undone = false;
    const to = setTimeout(async () => {
      delete delTimers.current[tk.id];
      try { await api.delete(`/support/tickets/${tk.id}/mine`); } catch { toast.error("Fehler"); loadMine(); }
    }, 5000);
    delTimers.current[tk.id] = to;
    toast(t("ticketDeleted"), { duration: 5000, action: { label: t("undo"), onClick: () => { if (undone) return; undone = true; clearTimeout(to); delete delTimers.current[tk.id]; loadMine(); } } });
  };

  useEffect(() => {
    if (supportEnabled) api.get("/support/tickets/mine").then((r) => setMyTickets(r.data)).catch(() => {});
  }, [supportEnabled, step]);
  const pickCategory = (c) => { setCategory(c); setStep("trouble"); };

  const uploadShot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/support/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShot({ path: r.data.path, name: file.name });
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!description.trim()) { toast.error("Bitte beschreibe dein Anliegen."); return; }
    setBusy(true);
    try {
      const r = await api.post("/support/tickets", {
        category, description, context_type: "app",
        screenshot_path: shot?.path || "", tech: techContext(),
      });
      setResult(r.data); setStep("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Senden"); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep("home"); setCategory("other"); setDescription(""); setShot(null); setResult(null); };

  if (!supportEnabled) {
    return (
      <Shell back="/settings">
        <div className="pt-24 text-center" data-testid="support-disabled">
          <Lifebuoy size={48} weight="duotone" className="mx-auto text-gray-400" />
          <p className="mt-3 font-heading text-gray-500">Der Support-Bereich ist derzeit nicht verfügbar.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell back="/settings">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="support-page">
        <h1 className="text-3xl font-heading font-semibold text-gray-900 flex items-center gap-2"><Lifebuoy size={30} weight="duotone" />Hilfe &amp; Support</h1>

        {step === "home" && (
          <>
            <AiChat onCreateTicket={() => setStep("form")} />
            <div className="sb-card p-5" data-testid="support-faq">
              <h2 className="font-heading font-semibold mb-2">Häufige Fragen</h2>
              <Accordion type="single" collapsible>
                {faqList.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-heading text-sm">{f.q}</AccordionTrigger>
                    <AccordionContent className="font-body text-sm text-gray-600">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="sb-card p-5" data-testid="support-wizard-start">
              <h2 className="font-heading font-semibold mb-1">Problem melden</h2>
              <p className="font-body text-sm text-gray-500 mb-3">Wähle einen Bereich – wir zeigen dir zuerst schnelle Lösungen.</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(([v, l]) => (
                  <button key={v} data-testid={`support-cat-${v}`} onClick={() => pickCategory(v)} className="border-2 border-gray-800 rounded-2xl px-3 py-2.5 bg-white text-left font-body text-sm active:scale-[0.98] transition-transform">{catLabel(v)}</button>
                ))}
              </div>
            </div>
            {myTickets.length > 0 && (
              <div className="sb-card p-5" data-testid="support-my-tickets">
                <h2 className="font-heading font-semibold mb-2">Meine Anfragen</h2>
                <div className="space-y-2">
                  {myTickets.map((tk) => (
                    <div key={tk.id} data-testid={`my-ticket-${tk.ticket_no}`} className="border-2 border-gray-800 rounded-2xl bg-white overflow-hidden">
                      <button data-testid={`my-ticket-toggle-${tk.ticket_no}`} onClick={() => setOpenTid(openTid === tk.id ? null : tk.id)} className="w-full text-left px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-heading text-sm font-bold">{tk.ticket_no}</p>
                          <p className="font-body text-xs text-gray-500 truncate">{tk.description}</p>
                        </div>
                        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: MY_STATUS_COLORS[tk.status] || "#6B7280" }}>{MY_STATUS_LABELS[tk.status] || tk.status}</span>
                      </button>
                      {openTid === tk.id && (
                        <div className="px-3 pb-3 space-y-2" data-testid={`my-ticket-replies-${tk.ticket_no}`}>
                          {editTid === tk.id ? (
                            <div className="space-y-2">
                              <textarea data-testid={`my-ticket-edit-input-${tk.ticket_no}`} className="sb-input" rows={3} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                              <div className="flex gap-2">
                                <button data-testid={`my-ticket-edit-save-${tk.ticket_no}`} onClick={() => saveEdit(tk)} disabled={!editDesc.trim()} className="sb-btn px-3 py-1.5 text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{t("save")}</button>
                                <button data-testid={`my-ticket-edit-cancel-${tk.ticket_no}`} onClick={() => setEditTid(null)} className="sb-btn px-3 py-1.5 bg-white text-sm">{t("cancel")}</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {(tk.replies || []).length === 0 && <p className="font-body text-xs text-gray-400">Noch keine Antwort vom Support.</p>}
                              {(tk.replies || []).map((r, i) => (
                                <p key={i} className="font-body text-xs text-gray-700 bg-gray-50 rounded-lg px-2 py-1.5">{r.text}</p>
                              ))}
                              {tk.status === "OPEN" ? (
                                <div className="flex gap-2 pt-1">
                                  <button data-testid={`my-ticket-edit-${tk.ticket_no}`} onClick={() => startEdit(tk)} className="sb-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1"><PencilSimple size={14} weight="bold" />{t("edit")}</button>
                                  <button data-testid={`my-ticket-delete-${tk.ticket_no}`} onClick={() => setConfirmDel(tk)} className="sb-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1 text-red-600"><Trash size={14} weight="bold" />{t("delete")}</button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-2 pt-1">
                                  <p className="font-body text-[11px] text-amber-600 flex items-center gap-1" data-testid={`my-ticket-locked-${tk.ticket_no}`}><Info size={13} weight="fill" />{t("ticketBeingHandled")}</p>
                                  <button data-testid={`my-ticket-delete-${tk.ticket_no}`} onClick={() => setConfirmDel(tk)} className="sb-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1 text-red-600"><Trash size={14} weight="bold" />{t("delete")}</button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {step === "trouble" && (
          <div className="sb-card p-5 space-y-3" data-testid="support-trouble">
            <h2 className="font-heading font-semibold">{catLabel(category)}</h2>
            <p className="font-body text-sm text-gray-500">Bitte probiere diese Schritte:</p>
            <ul className="space-y-2">
              {(cc?.support?.troubleHints?.[category] || TROUBLE[category] || []).map((s, i) => (
                <li key={i} className="flex gap-2 font-body text-sm text-gray-700"><CheckCircle size={18} weight="fill" className="text-green-500 shrink-0 mt-0.5" />{s}</li>
              ))}
            </ul>
            <p className="font-body text-sm font-medium pt-1">Hat das dein Problem gelöst?</p>
            <div className="flex gap-2">
              <button data-testid="trouble-yes" onClick={reset} className="sb-btn flex-1 py-2.5 bg-white font-medium flex items-center justify-center gap-1"><ThumbsUp size={16} />Ja, danke</button>
              <button data-testid="trouble-no" onClick={() => setStep("form")} className="sb-btn flex-1 py-2.5 text-white font-medium flex items-center justify-center gap-1" style={{ background: "var(--app-primary)" }}><ThumbsDown size={16} />Nein, Ticket öffnen</button>
            </div>
          </div>
        )}

        {step === "form" && (
          <div className="sb-card p-5 space-y-3" data-testid="support-form">
            <h2 className="font-heading font-semibold">Ticket erstellen</h2>
            <label className="font-body text-sm text-gray-600">Bereich
              <select data-testid="form-category" className="sb-input mt-1" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map(([v]) => <option key={v} value={v}>{catLabel(v)}</option>)}
              </select>
            </label>
            <label className="font-body text-sm text-gray-600">Beschreibung
              <textarea data-testid="form-description" className="sb-input mt-1" rows={4} placeholder="Was ist passiert? Wann tritt das Problem auf?" value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadShot} />
              <button data-testid="form-screenshot" onClick={() => fileRef.current?.click()} disabled={uploading} className="sb-btn w-full py-2.5 bg-white font-medium flex items-center justify-center gap-1 disabled:opacity-50">
                <ImageIcon size={18} />{uploading ? "Lädt…" : shot ? `Screenshot: ${shot.name}` : "Screenshot anhängen (optional)"}
              </button>
            </div>
            <p className="font-body text-[11px] text-gray-400">Technische Infos zu deinem Gerät werden automatisch mitgesendet, damit wir schneller helfen können.</p>
            <button data-testid="form-submit" onClick={submit} disabled={busy || !description.trim()} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-1" style={{ background: "var(--app-primary)" }}>
              <PaperPlaneRight size={18} />{busy ? "Wird gesendet…" : "Ticket absenden"}
            </button>
          </div>
        )}

        {step === "done" && result && (
          <div className="sb-card p-6 text-center space-y-2" data-testid="support-done">
            <CheckCircle size={48} weight="fill" className="mx-auto text-green-500" />
            <h2 className="font-heading font-semibold text-lg">Danke für deine Meldung!</h2>
            <p className="font-body text-sm text-gray-600">Deine Ticket-Nummer: <strong data-testid="done-ticket-no">{result.ticket_no}</strong></p>
            {result.error_id && <p className="font-body text-xs text-gray-400">Referenz: {result.error_id}</p>}
            <p className="font-body text-sm text-gray-600">Wir melden uns per E-Mail bei dir.</p>
            <button data-testid="done-back" onClick={reset} className="sb-btn w-full py-2.5 bg-white font-medium mt-2">Zurück zur Hilfe</button>
          </div>
        )}
      </motion.div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="my-ticket-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTicketTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteTicketDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-ticket-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-ticket-confirm" onClick={() => doDelete(confirmDel)} style={{ background: "#EF4444" }}>{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
